import React, { useMemo } from 'react';
import { Input } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import { CATEGORIES, CATEGORY_COLORS, getCategory, unicodeLabel, buildRoundedPath } from './glyphEditorState';

// Convert glyph path commands to an SVG path d-string
const pathToSVGd = (path) => {
    if (!path || path.length === 0) return '';
    // Apply corner rounding before converting to SVG
    const displayPath = buildRoundedPath(path);
    return displayPath.map(cmd => {
        switch (cmd.type) {
            case 'M': return `M ${cmd.x} ${cmd.y}`;
            case 'L': return `L ${cmd.x} ${cmd.y}`;
            case 'C': return `C ${cmd.x1} ${cmd.y1} ${cmd.x2} ${cmd.y2} ${cmd.x} ${cmd.y}`;
            case 'Q': return `Q ${cmd.x1} ${cmd.y1} ${cmd.x} ${cmd.y}`;
            case 'Z': return 'Z';
            default: return '';
        }
    }).join(' ');
};

// Mini SVG preview of a glyph path (font coords: y-up, but SVG is y-down so we flip)
const GlyphPreview = ({ path, advanceWidth = 600, size = 48, color = '#58a6ff' }) => {
    const d = pathToSVGd(path);
    if (!d) return null;
    // Use a viewBox that covers the typical glyph space: 0 to advanceWidth in x, -200 to 800 in y
    // We flip Y by using a negative scaleY transform
    const padding = 40;
    const vbX = -padding;
    const vbY = -800 - padding;
    const vbW = Math.max(advanceWidth, 400) + padding * 2;
    const vbH = 1000 + padding * 2;
    return (
        <svg width={size} height={size} viewBox={`${vbX} ${vbY} ${vbW} ${vbH}`}
            style={{ overflow: 'visible' }}>
            <g transform="scale(1, -1)">
                <path d={d} fill={color + '33'} stroke={color} strokeWidth={Math.max(vbW / 60, 8)}
                    fillRule="evenodd" />
            </g>
        </svg>
    );
};

/**
 * GlyphLibraryView - Grid of all glyphs with category filtering (Runebender-style)
 */
const GlyphLibraryView = ({ glyphs, selectedGlyph, onSelectGlyph, onEditGlyph, searchText, onSearchChange, categoryFilter, onCategoryChange }) => {

    const filteredGlyphs = useMemo(() => {
        let filtered = glyphs;
        if (categoryFilter !== 'all') {
            filtered = filtered.filter(g => getCategory(g.unicode) === categoryFilter);
        }
        if (searchText) {
            const lower = searchText.toLowerCase();
            filtered = filtered.filter(g =>
                g.name.toLowerCase().includes(lower) ||
                String.fromCharCode(g.unicode).toLowerCase().includes(lower) ||
                unicodeLabel(g.unicode).toLowerCase().includes(lower)
            );
        }
        return filtered;
    }, [glyphs, categoryFilter, searchText]);

    const selected = selectedGlyph;

    return (
        <div style={{ display: 'flex', height: '100%', overflow: 'hidden' }}>
            {/* Left Sidebar - Categories */}
            <div style={{
                width: 160, flexShrink: 0, borderRight: '1px solid #30363d',
                padding: 12, display: 'flex', flexDirection: 'column', gap: 2,
                background: '#0d1117'
            }}>
                <div style={{ color: '#8b949e', fontSize: 11, fontWeight: 600, marginBottom: 8, letterSpacing: 0.5 }}>CATEGORIES</div>
                {CATEGORIES.map(cat => (
                    <div
                        key={cat.value}
                        onClick={() => onCategoryChange(cat.value)}
                        style={{
                            padding: '8px 12px', borderRadius: 6, cursor: 'pointer',
                            background: categoryFilter === cat.value ? '#1f6feb22' : 'transparent',
                            color: categoryFilter === cat.value ? '#58a6ff' : '#c9d1d9',
                            fontSize: 13, fontWeight: categoryFilter === cat.value ? 600 : 400,
                            transition: 'all 0.15s',
                            border: categoryFilter === cat.value ? '1px solid #1f6feb44' : '1px solid transparent',
                        }}
                    >
                        {cat.label}
                    </div>
                ))}
                <div style={{ flex: 1 }} />
                <div style={{ color: '#484f58', fontSize: 11, textAlign: 'center' }}>
                    {filteredGlyphs.length} glyphs
                </div>
            </div>

            {/* Center - Glyph Grid */}
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                {/* Search */}
                <div style={{ padding: 12, borderBottom: '1px solid #30363d', flexShrink: 0 }}>
                    <Input
                        prefix={<SearchOutlined style={{ color: '#484f58' }} />}
                        placeholder="Search glyphs..."
                        value={searchText}
                        onChange={e => onSearchChange(e.target.value)}
                        style={{ background: '#0d1117', border: '1px solid #30363d', color: '#c9d1d9', borderRadius: 6 }}
                    />
                </div>
                {/* Grid */}
                <div style={{ flex: 1, overflowY: 'auto', padding: 12 }}>
                    <div style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fill, minmax(90px, 1fr))',
                        gap: 6,
                    }}>
                        {filteredGlyphs.map(g => {
                            const cat = getCategory(g.unicode);
                            const color = CATEGORY_COLORS[cat] || '#8b949e';
                            const isSelected = selected && selected.unicode === g.unicode;
                            const hasPath = g.path && g.path.length > 0;
                            return (
                                <div
                                    key={g.unicode}
                                    onClick={() => onSelectGlyph(g)}
                                    onDoubleClick={() => onEditGlyph(g)}
                                    style={{
                                        background: isSelected ? '#1f6feb22' : '#161b22',
                                        border: isSelected ? '2px solid #1f6feb' : '2px solid #30363d',
                                        borderRadius: 8, cursor: 'pointer',
                                        display: 'flex', flexDirection: 'column',
                                        alignItems: 'center', justifyContent: 'center',
                                        padding: '12px 4px 8px',
                                        transition: 'all 0.15s',
                                        opacity: hasPath ? 1 : 0.5,
                                        minHeight: 100,
                                    }}
                                >
                                    {hasPath ? (
                                        <div style={{ marginBottom: 6 }}>
                                            <GlyphPreview path={g.path} advanceWidth={g.advanceWidth} size={48} color={color} />
                                        </div>
                                    ) : (
                                        <div style={{
                                            fontSize: 32, fontWeight: 700, color: '#484f58',
                                            lineHeight: 1, marginBottom: 6,
                                            fontFamily: 'system-ui',
                                        }}>
                                            {g.unicode >= 33 ? String.fromCharCode(g.unicode) : '␣'}
                                        </div>
                                    )}
                                    <div style={{ fontSize: 10, color: '#8b949e', textAlign: 'center' }}>
                                        {g.name}
                                    </div>
                                    <div style={{ fontSize: 9, color: '#484f58', fontFamily: 'monospace' }}>
                                        {unicodeLabel(g.unicode)}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>

            {/* Right Sidebar - Glyph Info */}
            <div style={{
                width: 200, flexShrink: 0, borderLeft: '1px solid #30363d',
                padding: 16, background: '#0d1117',
                display: 'flex', flexDirection: 'column', gap: 12,
            }}>
                {selected ? (
                    <>
                        <div style={{ color: '#8b949e', fontSize: 11, fontWeight: 600, letterSpacing: 0.5 }}>GLYPH INFO</div>
                        <div style={{
                            width: '100%', aspectRatio: '1', background: '#161b22',
                            borderRadius: 8, border: '1px solid #30363d',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                        }}>
                            {selected.path && selected.path.length > 0 ? (
                                <GlyphPreview path={selected.path} advanceWidth={selected.advanceWidth} size={120}
                                    color={CATEGORY_COLORS[getCategory(selected.unicode)] || '#58a6ff'} />
                            ) : (
                                <span style={{
                                    fontSize: 72, color: CATEGORY_COLORS[getCategory(selected.unicode)] || '#fff',
                                    fontFamily: 'system-ui',
                                }}>
                                    {selected.unicode >= 33 ? String.fromCharCode(selected.unicode) : '␣'}
                                </span>
                            )}
                        </div>
                        {[
                            ['Glyph Name', selected.name],
                            ['Unicode', unicodeLabel(selected.unicode)],
                            ['Width', selected.advanceWidth],
                            ['Contours', selected.path ? selected.path.filter(c => c.type === 'M').length : 0],
                            ['Points', selected.path ? selected.path.length : 0],
                        ].map(([label, value]) => (
                            <div key={label}>
                                <div style={{ fontSize: 10, color: '#484f58', marginBottom: 2 }}>{label}</div>
                                <div style={{ fontSize: 13, color: '#c9d1d9', fontFamily: 'monospace' }}>{value}</div>
                            </div>
                        ))}
                    </>
                ) : (
                    <div style={{ color: '#484f58', fontSize: 12, textAlign: 'center', marginTop: 40 }}>
                        Select a glyph to view info
                    </div>
                )}
            </div>
        </div >
    );
};

export default GlyphLibraryView;
